import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Container, Button, Table, InputGroup, FormControl, Form, Modal } from 'react-bootstrap';
import { Plus, Edit2, Trash2, Tag, Search, Trash, CircleAlert } from 'lucide-react';
import style from './Categories.module.scss';
import { toast } from 'react-toastify';
import fetchCategories from '../../services/categories/fetchCategories';
import createCategory from '../../services/categories/createCategory';
import updateCategory from '../../services/categories/updateCategory';
import deleteCategory from '../../services/categories/deleteCategory';
import CreateCategoryModal from './CreateCategoryModal';
import UpdateCategoryModal from './UpdateCategoryModal';

function Categories() {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  
  // States for multiple selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchCategories();
        setCategories(response.data || []);
        setFilteredCategories(response.data || []);
      } catch (error) {
        toast.error('Failed to load categories');
        console.error(error);
      } finally {
        setFetchLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Search functionality
  useEffect(() => {
    const filtered = categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredCategories(filtered);
    setPageNumber(1);
  }, [categories, searchTerm]);

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleShowUpdateModal = (category) => {
    setEditingCategory(category);
    setShowUpdateModal(true);
  };

  const handleCreateCategory = async (formData) => {
    setLoading(true);
    try {
      const response = await createCategory(formData);
      setCategories(prev => [...prev, response.data]);
      setFilteredCategories(prev => [...prev, response.data]);
      toast.success('Category added successfully');
      setShowCreateModal(false);
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const firstError = validationErrors[0]?.msg || 'Validation failed';
        toast.error(firstError);
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to save category');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (formData) => {
    setLoading(true);
    try {
      const response = await updateCategory(editingCategory.id, formData);
      setCategories(prev =>
        prev.map(cat =>
          cat.id === editingCategory.id ? response.data : cat
        )
      );
      setFilteredCategories(prev =>
        prev.map(cat =>
          cat.id === editingCategory.id ? response.data : cat
        )
      );
      toast.success('Category updated successfully');
      setShowUpdateModal(false);
      setEditingCategory(null);
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const firstError = validationErrors[0]?.msg || 'Validation failed';
        toast.error(firstError);
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to save category');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    setEditingCategory(category);
    setShowDeleteModal(true);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredCategories.length / pageSize);
  const startIndex = (pageNumber - 1) * pageSize;
  const currentCategories = filteredCategories.slice(startIndex, startIndex + pageSize);

  // Function for selecting all checkboxes
  const selectAllCheckBox = () => {
    const allIds = currentCategories.map(category => category.id);
    if (selectAll) {
      setSelectedIds([]);
    }
    else {
      setSelectedIds(allIds);
    }
    setSelectAll(!selectAll);
  }

  // Function for handling single checkbox selection
  const handleSingleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(_id => _id !== id));
    }
    else {
      setSelectedIds(prev => [...prev, id])
    }
  }

  // Delete multiple categories
  const deleteMultipleCategories = async () => {
    // Show confirmation modal for multiple deletion
    setEditingCategory({ 
      id: 'multiple', 
      name: `${selectedIds.length} categor${selectedIds.length > 1 ? 'ies' : 'y'}`,
      isMultiple: true 
    });
    setShowDeleteModal(true);
  };

  // Actual delete functions
  const confirmDelete = async (item) => {
    try {
      if (item.isMultiple) {
        // Delete multiple categories
        const deletionPromises = selectedIds.map(id => deleteCategory(id));
        await Promise.all(deletionPromises);

        setCategories(prev => prev.filter(cat => !selectedIds.includes(cat.id)));
        setFilteredCategories(prev => prev.filter(cat => !selectedIds.includes(cat.id)));
        
        setSelectedIds([]);
        setSelectAll(false);
        toast.success(`${selectedIds.length} categor${selectedIds.length > 1 ? 'ies' : 'y'} deleted successfully`);
      } else {
        // Delete single category
        await deleteCategory(item.id);
        setCategories(prev => prev.filter(cat => cat.id !== item.id));
        setFilteredCategories(prev => prev.filter(cat => cat.id !== item.id));
        toast.success('Category deleted successfully');
      }
      
      setShowDeleteModal(false);
      setEditingCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
      console.error(error);
    }
  };

  return (
    <Container fluid className={style.categoriesContainer}>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">Categories Management</h4>
              <p className="text-muted mb-0">
                Manage product categories for your restaurant
              </p>
            </div>
          </div>
          
          {/* Search Bar and Add Button in same row */}
          <div className={`${style.SearchButton} d-flex flex-column flex-md-row mb-4 gap-2 justify-content-between`}>
            <InputGroup className={`${style.searchBox} `} >
              <InputGroup.Text><Search size={16} /></InputGroup.Text>
              <FormControl
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <div className={`${style.addButton} d-flex gap-2`}>
              {/* Add Category Button */}
              <Button
                variant="primary"
                onClick={handleShowCreateModal}
                className="d-flex align-items-center justify-content-center gap-2 w-100"
              >
                <Plus size={15} />
                Add Category
              </Button>
              {selectedIds.length > 0 && <div className={`pt-1 ${style.selectedTrash}`} onClick={() => deleteMultipleCategories()}><Trash/></div>}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card className={style.categoriesCard}>
            <Card.Body>
              <div className="table-responsive">
                <Table hover className={style.categoriesTable}>
                  <thead>
                    <tr>
                      <th>
                        <Form.Check 
                          type="checkbox"
                          onClick={() => selectAllCheckBox()}
                          id={`select-all-checkbox`}
                        />
                      </th>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {fetchLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="spinner-border text-primary" />
                          <p className="mt-2 text-muted">Loading categories...</p>
                        </td>
                      </tr>
                    ) : filteredCategories.length > 0 ? (
                      currentCategories.map(category => (
                        <tr key={category.id}>
                          <td>
                            <Form.Check 
                              type="checkbox"
                              checked={selectedIds.includes(category.id)}
                              onChange={() => handleSingleSelect(category.id)}
                              id={`category-checkbox-${category.id}`}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Tag size={16} className="text-primary" />
                              <span className="fw-medium">{category.name}</span>
                            </div>
                          </td>
                          <td className="text-muted">
                            {category.description || '—'}
                          </td>
                          <td>
                            {new Date(category.createdAt).toLocaleDateString('en-IN')}
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-center">
                              <div 
                                className="bg-warning cursor-pointer d-flex align-items-center justify-content-center" 
                                style={{ width: '32px', height: '32px', borderRadius: '4px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowUpdateModal(category);
                                }}
                                title="Edit Category"
                              >
                                <Edit2 size={16} color="white" />
                              </div>
                              
                              <div 
                                className="bg-danger cursor-pointer d-flex align-items-center justify-content-center" 
                                style={{ width: '32px', height: '32px', borderRadius: '4px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(category.id);
                                }}
                                title="Delete Category"
                              >
                                <Trash2 size={16} color="white" />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <Tag size={48} className="mb-2 d-block mx-auto opacity-50" />
                          <p>No categories found</p>
                          <Button variant="primary" onClick={handleShowCreateModal}>
                            Add Your First Category
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Row className="mt-3">
          <Col className="d-flex justify-content-center">
            <div className="d-flex justify-content-center align-items-center gap-3">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
              >
                &laquo;
              </Button>

              <span>
                Page <strong>{pageNumber}</strong> of {totalPages}
              </span>

              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pageNumber === totalPages}
                onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))}
              >
                &raquo;
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {/* Create Category Modal */}
      <CreateCategoryModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateCategory}
        loading={loading}
      />

      {/* Update Category Modal */}
      <UpdateCategoryModal
        show={showUpdateModal}
        onHide={() => {
          setShowUpdateModal(false);
          setEditingCategory(null);
        }}
        onSubmit={handleUpdateCategory}
        loading={loading}
        category={editingCategory}
      />

      {/* Delete Category Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setEditingCategory(null);
        }}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Body className="d-flex flex-column align-items-center text-center">
          <h5 className="mb-3">
            Are you sure you want to delete {editingCategory?.isMultiple ? editingCategory.name : `this category`}?
          </h5>
      
          <div className="mb-4">
            <CircleAlert size={48} className="text-danger" />
          </div>
      
          <div className="d-flex justify-content-center gap-3">
            <Button variant="secondary" onClick={() => {
              setShowDeleteModal(false);
              setEditingCategory(null);
            }}>No</Button>
            <Button variant="danger" onClick={() => confirmDelete(editingCategory)}>Yes</Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Categories;
